import { useState, useEffect } from 'react';
import { IconRefresh, IconSearch, IconLoader2 } from '@tabler/icons-react';
import { xeroApiService, XeroContact } from '../lib/xeroApi';
import PageHeader from '../components/PageHeader';
import MainCard from '../components/MainCard';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export function XeroContactsPage() {
  const [contacts, setContacts] = useState<XeroContact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tenantId, setTenantId] = useState<string>('2130c79b-608e-45ef-a5dd-801bc5d53cf8');
  const [pageSize, setPageSize] = useState<number>(10);
  const [status, setStatus] = useState<'ACTIVE' | 'ARCHIVED' | 'GDPRREQUEST' | ''>('ACTIVE');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [nextPageInfo, setNextPageInfo] = useState<string | null>(null);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageHistory, setPageHistory] = useState<string[]>([]);

  const loadContacts = async (pageInfo?: string, isBackNavigation = false) => {
    if (!tenantId.trim()) {
      setError('Tenant ID is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await xeroApiService.getContacts({
        tenantId: tenantId.trim(),
        pageSize,
        status: status || undefined,
        pageInfo,
      });

      if (response.success && response.data) {
        setContacts(response.data.contacts || []);
        setNextPageInfo(response.data.pagination.nextPageInfo || null);
        setHasNextPage(response.data.pagination.hasNextPage || false);
        setTotal(response.data.pagination.total || 0);

        if (pageInfo && !isBackNavigation) {
          setPageHistory((prev) => [...prev, pageInfo]);
        } else if (isBackNavigation) {
          setPageHistory((prev) => prev.slice(0, -1));
        }
      } else {
        setError(response.message || 'Failed to load contacts');
      }
    } catch (err: any) {
      console.error('Error loading contacts:', err);
      setError(
        err.response?.data?.message ||
        err.message ||
        'Failed to load contacts via API Gateway.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, pageSize, status]);

  const handleRefresh = () => {
    setPageHistory([]);
    setCurrentPage(1);
    loadContacts();
  };

  const handleNextPage = () => {
    if (nextPageInfo) {
      setCurrentPage((prev) => prev + 1);
      loadContacts(nextPageInfo, false);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setPageHistory([]);
      setCurrentPage(1);
      loadContacts(undefined, true);
    }
  };

  const handleStatusChange = (newStatus: string | null) => {
    setStatus((newStatus?.trim() || '') as typeof status);
    setPageHistory([]);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (newPageSize: string | null) => {
    setPageSize(Number(newPageSize) || 10);
    setPageHistory([]);
    setCurrentPage(1);
  };

  const filteredContacts = contacts.filter((contact) => {
    if (!searchTerm.trim()) return true;
    const search = searchTerm.toLowerCase();
    return (
      contact.name?.toLowerCase().includes(search) ||
      contact.email?.toLowerCase().includes(search) ||
      contact.id?.toLowerCase().includes(search) ||
      contact.taxNumber?.toLowerCase().includes(search)
    );
  });

  const formatDate = (dateString: string) => {
    try {
      const match = dateString.match(/\/Date\((\d+)/);
      if (match) {
        const timestamp = parseInt(match[1], 10);
        return new Date(timestamp).toLocaleString();
      }
      return dateString;
    } catch {
      return dateString;
    }
  };

  const getAddressString = (addresses: XeroContact['addresses']) => {
    const streetAddress = addresses.find((a) => a.type === 'STREET');
    if (streetAddress) {
      const parts = [
        streetAddress.city,
        streetAddress.region,
        streetAddress.postalCode,
        streetAddress.country,
      ].filter(Boolean);
      return parts.join(', ') || '-';
    }
    return '-';
  };

  const getPhoneString = (phones: XeroContact['phones']) => {
    const defaultPhone = phones.find((p) => p.type === 'DEFAULT' && p.number);
    const mobilePhone = phones.find((p) => p.type === 'MOBILE' && p.number);
    return (defaultPhone?.number || mobilePhone?.number || '-');
  };

  return (
    <div>
      <PageHeader
        title="Xero Contacts"
        subtitle="View and manage Xero contacts via API Gateway"
        action={
          <Button variant="outline" onClick={handleRefresh} disabled={loading}>
            <IconRefresh className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        }
      />

      <MainCard className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 min-w-[300px]">
            <Label htmlFor="tenant-id" className="mb-1.5 block text-sm">Tenant ID</Label>
            <Input
              id="tenant-id"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
            />
          </div>
          <div className="min-w-[150px]">
            <Label className="mb-1.5 block text-sm">Status</Label>
            <Select value={status} onValueChange={handleStatusChange}>
              <SelectTrigger>
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value=" ">All</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="ARCHIVED">Archived</SelectItem>
                <SelectItem value="GDPRREQUEST">GDPR Request</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[120px]">
            <Label className="mb-1.5 block text-sm">Page Size</Label>
            <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <Label className="mb-1.5 block text-sm">Search</Label>
            <div className="relative">
              <IconSearch className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search contacts..."
                className="pl-9"
              />
            </div>
          </div>
        </div>
      </MainCard>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <button onClick={() => setError(null)} className="text-sm underline ml-4">Dismiss</button>
          </AlertDescription>
        </Alert>
      )}

      <MainCard>
        {loading ? (
          <div className="flex justify-center p-8">
            <IconLoader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="p-6">
            <Alert>
              <AlertDescription>No contacts found</AlertDescription>
            </Alert>
          </div>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-bold">Name</TableHead>
                  <TableHead className="font-bold">Status</TableHead>
                  <TableHead className="font-bold">Email</TableHead>
                  <TableHead className="font-bold">Phone</TableHead>
                  <TableHead className="font-bold">Address</TableHead>
                  <TableHead className="font-bold">Type</TableHead>
                  <TableHead className="font-bold">Tax Number</TableHead>
                  <TableHead className="font-bold">Updated</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContacts.map((contact) => (
                  <TableRow key={contact.id} className="hover:bg-muted/50">
                    <TableCell>
                      <p className="text-sm font-medium">{contact.name || '-'}</p>
                      <p className="text-xs text-muted-foreground">{contact.id}</p>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          contact.status === 'ACTIVE'
                            ? 'success'
                            : contact.status === 'ARCHIVED'
                            ? 'secondary'
                            : 'outline'
                        }
                      >
                        {contact.status}
                      </Badge>
                    </TableCell>
                    <TableCell>{contact.email || '-'}</TableCell>
                    <TableCell>{getPhoneString(contact.phones)}</TableCell>
                    <TableCell>{getAddressString(contact.addresses)}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        {contact.isCustomer && (
                          <Badge>Customer</Badge>
                        )}
                        {contact.isSupplier && (
                          <Badge variant="secondary">Supplier</Badge>
                        )}
                        {!contact.isCustomer && !contact.isSupplier && (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{contact.taxNumber || '-'}</TableCell>
                    <TableCell>
                      <span className="text-sm">{formatDate(contact.updatedDate)}</span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="p-4 flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                Showing {filteredContacts.length} of {total} contacts
                {searchTerm && ` (filtered from ${contacts.length} loaded)`}
              </p>
              <div className="flex items-center gap-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1 || loading}
                >
                  Previous
                </Button>
                <span className="text-sm">Page {currentPage}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={!hasNextPage || loading}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </MainCard>
    </div>
  );
}
